'use client'
import { useLottie } from 'lottie-react'
import { AnimationEventCallback, AnimationItem } from 'lottie-web'
import { FC, useEffect, useRef } from 'react'

interface AnimationElementProps {
  animationData: any
  loop?: boolean
  autoplay?: boolean
  onLoopComplete?: AnimationEventCallback
  onComplete?: () => void
  startFrame?: number
  endFrame?: number
  direction?: 1 | -1
  play?: boolean
  renderer?: 'svg' | 'canvas' // 렌더링 방식 선택
  [x: string]: any
}

const AnimationComponent: FC<AnimationElementProps> = ({
  animationData,
  loop = false,
  autoplay = true,
  onLoopComplete,
  onComplete,
  startFrame,
  endFrame,
  direction = 1,
  play,
  renderer = 'svg', // 기본값은 svg
  preserveAspectRatio = 'xMidYMid slice',
  className,
  ...rest
}) => {
  const animationRef = useRef<AnimationItem | null>(null)

  // renderer에 따른 최적화 설정
  const getRendererSettings = () => {
    const baseSettings = { preserveAspectRatio }

    if (renderer === 'canvas') {
      // Canvas 렌더러용 모바일 최적화 설정
      return {
        ...baseSettings,
        clearCanvas: true, // Canvas 메모리 정리
        progressiveLoad: false, // 단순 애니메이션용
        hideOnTransparent: true, // 투명 영역 최적화
      }
    }

    return baseSettings
  }

  const options = {
    animationData,
    loop,
    autoplay,
    onComplete,
    onLoopComplete,
    className,
    renderer, // 렌더링 방식 설정
    rendererSettings: getRendererSettings(),
    ...rest,
  }

  const { View, animationItem } = useLottie(options)

  // animationItem 참조 저장 (제어용)
  useEffect(() => {
    if (animationItem) {
      animationRef.current = animationItem
    }
  }, [animationItem, autoplay])

  // 프레임 범위, 방향, 재생/정지 제어
  useEffect(() => {
    const anim = animationRef.current
    if (!anim) return
    if (!play) return

    if (direction !== undefined) {
      anim.setDirection(direction)
    }

    if (startFrame !== undefined && endFrame !== undefined && startFrame !== endFrame) {
      anim.playSegments([startFrame, endFrame], true)
      return
    }

    if (play !== undefined) {
      if (play) {
        anim.play()
      } else {
        anim.pause()
      }
    }
  }, [startFrame, endFrame, direction, play])

  return View ? <>{View}</> : null
}

export default AnimationComponent
